import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Stack,
  Typography,
  InputBase,
  Box,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { ContentModal } from "components/Surfaces/ContentModal/ContentModal";
import { toast } from "react-toastify";

const COLORS = {
  navy: "#053462",
  blue: "#2CABF9",
  lightBlueBg: "#EAF6FE",
  grayText: "#7A8699",
  inputBg: "#F5F7FA",
  unselectedCircle: "#C7D0DB",
};

/**
 * Linha selecionável de uma unidade (ou a opção "Todos os endereços").
 */
function UnitRow({ title, subtitle, selected, onClick }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      onClick={onClick}
      sx={{
        cursor: "pointer",
        border: `1px solid ${selected ? COLORS.blue : COLORS.navy}`,
        borderRadius: "8px",
        padding: "16px",
        backgroundColor: selected ? COLORS.lightBlueBg : "#fff",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
      }}
    >
      <Stack gap="4px">
        <Typography
          fontSize="14px"
          fontWeight={700}
          color={selected ? COLORS.blue : COLORS.navy}
          sx={{ textTransform: "uppercase", lineHeight: "100%" }}
        >
          {title}
        </Typography>
        <Typography
          fontSize="12px"
          fontWeight={400}
          color={COLORS.grayText}
          sx={{ lineHeight: "100%" }}
        >
          {subtitle}
        </Typography>
      </Stack>
      <Box
        sx={{
          width: "24px",
          height: "24px",
          minWidth: "24px",
          borderRadius: "50%",
          border: `1.5px solid ${
            selected ? COLORS.blue : COLORS.unselectedCircle
          }`,
          backgroundColor: selected ? COLORS.blue : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {selected && <CheckIcon sx={{ color: "#fff", fontSize: 16 }} />}
      </Box>
    </Stack>
  );
}

/**
 * Modal "Vincular Unidades".
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {Array<{ endereco: string, cidade: string, estado: string, "local-entrega": string }>} addresses
 *   Lista de endereços (equivalente a `localEntrega`).
 * @param {(selectedLocalEntregaCodes: string[]) => void} onConfirm
 *   Chamado ao clicar em "Avançar" dentro do modal, recebendo os códigos
 *   `local-entrega` de todos os endereços selecionados.
 */
export function LinkUnitsModal({
  open,
  onClose,
  addresses = [],
  onConfirm,
  initialSelected = [],
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);

  // Reseta busca e seleção sempre que o modal é reaberto
  useEffect(() => {
    if (open) {
      setSearch("");
      setSelected(initialSelected);
    }
  }, [open]);

  const filteredAddresses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return addresses;

    return addresses.filter((item) => {
      const label = `${item.endereco ?? ""} ${item.cidade ?? ""} ${
        item.estado ?? ""
      }`.toLowerCase();
      return label.includes(term);
    });
  }, [addresses, search]);

  const allSelected =
    addresses.length > 0 && selected.length === addresses.length;

  function toggleAll() {
    setSelected(
      allSelected ? [] : addresses.map((item) => item["local-entrega"])
    );
  }

  function toggleOne(code) {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  }

  function handleAdvance() {
    if (selected.length === 0) {
      toast.error("Selecione ao menos uma unidade");
      return;
    }
    onConfirm(selected);
  }

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus>
      <ContentModal
        minWidth={"544px"}
        maxWidth={"544px"}
        style={{
          width: "auto",
          paddingTop: "18px",
          paddingBottom: "18px",
          display: "flex",
          flexDirection: "column",
        }}
        borderRadius={"8px"}
        px={"16px"}
      >
        <Typography fontSize="24px" fontWeight={700} color={COLORS.navy}>
          Vincular Unidades
        </Typography>

        <Typography
          fontSize="14px"
          fontWeight={400}
          color={COLORS.navy}
          mt="12px"
          lineHeight="140%"
        >
          Selecione as unidades às quais este colaborador terá acesso. É
          possível vincular mais de uma unidade.
        </Typography>

        <Box
          sx={{
            mt: "24px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: COLORS.inputBg,
            borderRadius: "8px",
            padding: "10px 16px",
          }}
        >
          <SearchIcon sx={{ color: COLORS.navy, fontSize: 25 }} />
          <InputBase
            placeholder="Buscar unidade..."
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: "14px", color: COLORS.navy, display: "block" }}
            inputProps={{ style: { textAlign: "left" } }}
          />
        </Box>

        <Typography
          fontSize="12px"
          fontWeight={400}
          color={COLORS.grayText}
          mt="12px"
          mb="12px"
        >
          {filteredAddresses.length}{" "}
          {filteredAddresses.length === 1
            ? "unidade encontrada"
            : "unidades encontradas"}
        </Typography>

        <Stack
          gap="12px"
          sx={{ maxHeight: "320px", overflowY: "auto", pr: "4px" }}
        >
          <UnitRow
            title="TODOS OS ENDEREÇOS"
            subtitle="Selecionar todas as unidades"
            selected={allSelected}
            onClick={toggleAll}
          />

          {filteredAddresses.map((item) => {
            const code = item["local-entrega"];
            const isSelected = selected.includes(code);
            return (
              <UnitRow
                key={code}
                title={item.endereco}
                subtitle={`${item.cidade}/${item.estado}`}
                selected={isSelected}
                onClick={() => toggleOne(code)}
              />
            );
          })}
        </Stack>

        <Button
          variant="contained"
          //fullWidth
          style={{ width: "70%", marginLeft: "auto", marginRight: "auto" }}
          onClick={handleAdvance}
          sx={{
            mt: "24px",
            backgroundColor: COLORS.blue,
            color: "#fff",
            height: "48px",
            borderRadius: "36px",
            fontWeight: 700,
            fontSize: "16px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#1C9BE8",
              boxShadow: "none",
            },
          }}
        >
          Avançar
        </Button>
      </ContentModal>
    </Modal>
  );
}
